import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAuthentication, useUnsavedChanges } from '@connect-x-app/hybrid-state';
import { AdminConfigPermissions, SmartAction } from '@connect-x-app/shared-domain-models';
import { useBreadcrumbs, useForm } from '@connect-x-app/web-state';
import { Spinner } from '@connect-x-app/web-ui';

import { validateSmartActionForm } from '../hooks/useSmartActionValidation';
import { useSmartAction, useUpdateSmartAction } from '../queries';
import { SmartActionForm } from './SmartActionForm';

type FieldsProps = {
  canWrite: boolean;
  onSubmit: (smartAction: SmartAction) => void;
  smartAction: SmartAction;
};

/**
 * Owns the form. Split out so `useForm` is only ever called once the smart action has loaded —
 * hooks cannot sit behind an early return, and seeding the form with `undefined` used to push an
 * undefined value through validation.
 */
const UpdateSmartActionFields = ({ canWrite, onSubmit, smartAction }: FieldsProps) => {
  const isSystemManaged = smartAction.isDefault;

  const form = useForm<SmartAction>({
    initialForm: smartAction,
    submitForm: values => onSubmit(values),
    validateForm: values => validateSmartActionForm(values),
  });

  return <SmartActionForm canSave={canWrite && !isSystemManaged} form={form} isSystemManaged={isSystemManaged} />;
};

export const UpdateSmartAction = () => {
  const { t } = useTranslation();
  const updateBreadCrumbs = useBreadcrumbs(state => state.findAndReplace);

  const navigate = useNavigate();

  const { id } = useParams();

  const { data, isLoading: isLoadingSmartAction } = useSmartAction(id ?? '');

  const { hasPermission } = useAuthentication();

  const { setHasUnsavedChanges } = useUnsavedChanges();

  const { isPending, mutate } = useUpdateSmartAction({
    onSuccess: async () => {
      await setHasUnsavedChanges(false);
      return navigate('/admin/connect');
    },
  });

  useEffect(() => {
    if (data?.name) {
      updateBreadCrumbs(id, { name: data?.name, url: `/admin/connect/general/smart-actions/edit/${id}` });
    }
  }, [data, id, updateBreadCrumbs]);

  useEffect(() => {
    if (!id) {
      navigate('smart-actions');
    }
  }, [id, navigate]);

  if (isPending || isLoadingSmartAction || !data) {
    return <Spinner>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  return (
    <main className="smart-action-container">
      <UpdateSmartActionFields canWrite={hasPermission(AdminConfigPermissions.WRITE)} onSubmit={mutate} smartAction={data} />
    </main>
  );
};
